/**
 * MulticellStage — Phase 2, "Multicell".
 *
 * Slice 3: bigger world + camera + minimap. The play area is now a
 * fixed 4800×4800 rectangle (~4× a typical 1200×800 viewport), the
 * camera centers on the cluster with edge-clamping so the void
 * outside the world never enters the playfield, and a small minimap
 * in the bottom-right shows where the cluster is in the wider sea.
 *
 * The cluster's coordinates are now world-space. Mouse-follow
 * targets the world point under the cursor (cluster.x + (mouseX -
 * screenCenterX)), so control feel is camera-independent.
 *
 * Future slices add food, predators, cell types, and the actual
 * 1,000,000 biomass goal.
 */

import { Container, Graphics, Text } from 'pixi.js';
import type { Stage, StageContext } from '@/core/StageManager';
import { stageManager } from '@/core/StageManager';
import { bus } from '@/core/EventBus';
import { saveManager } from '@/core/SaveManager';
import { player } from '@/systems/PlayerState';
import type { CodexEntry } from '@/data/types';
import { BiofieldMesh } from '@/shaders/biofield';
import { ParticleSystem } from '@/systems/ParticleSystem';
import { AchievementSystem } from '@/systems/AchievementSystem';
import { MetaUpgradeState } from '@/systems/MetaUpgradeState';
import { Cluster, type CellType } from './cluster';
import { NutrientField } from './food';
import { ObstacleField } from './obstacles';
import { PredatorField } from './predators';
import { StormField } from './storm';
import { VERSION } from '@/version';
import { describeScoreResult } from '@/score/format';
// Pull in the same stylesheet Phase 1 uses so the DOM HUD strip below
// renders identically across stages without depending on Phase 1
// having been loaded first.
import '@/ui/ui.css';
import { CodexSpotlight, type SpotlightTarget } from '@/ui/CodexSpotlight';
import { createTouchPad, IS_TOUCH, type TouchPad } from '@/ui/TouchControls';
import { messageScale } from '@/ui/messageScale';

interface CarryoverPayload {
  fromStage?: string;
  totalDnaCarry?: number;
  biomassHeadStart?: number;
  speedTier?: string;
  speedMult?: number;
  baseDna?: number;
  speedBonusDna?: number;
  codexBonusDna?: number;
  completedAt?: string;
  elapsed?: number;
  codexUnlockedTotal?: number;
}

const WORLD_W = 4800;
const WORLD_H = 4800;
/** Camera zoom range. Touch screens start at ZOOM_MIN — the widest view —
 *  and the player pinches in from there; desktop starts at 1. */
const ZOOM_MIN = 0.35;
const ZOOM_MAX = 1.6;
function startZoom(): number {
  return IS_TOUCH ? ZOOM_MIN : 1;
}

// How close to the world edge the cluster can get before the hard
// clamp stops it. A small pad keeps the cluster's halo from clipping.
const CLUSTER_BOUND_PAD = 80;

// Camera deadzone — expressed as a fraction of the viewport so the
// X and Y feel match each other regardless of screen aspect ratio.
const CAM_DEADZONE_FRAC_X = 0.55;
const CAM_DEADZONE_FRAC_Y = 0.55;

const MINIMAP_SIZE = 140;
const MINIMAP_PAD = 16;

// Phase 2 win target. Carryover from Phase 1 lands the player at
// 0–20K so they always have a runway to the goal. 3M is tuned for
// roughly an hour of play with the 10-tier parasite economy:
// pellet nibbling won't get you there alone, and eating one of every
// parasite once is ~1M, so you need 2-3 cycles + steady pellet sweeps.
const STAGE_GOAL_BIOMASS = 3_000_000;
const NUTRIENT_COUNT = 480;
// Crystal shards densely pack the world — at this count any view of
// the world frames at least 2 shards on screen, so the world reads
// as a junkyard of alien wreckage rather than an empty bowl. ~16
// pellets per ring keeps the rings legible at this density.
const OBSTACLE_COUNT = 30;

// Predators are tiered (Minnow → Apex). Population is fixed in
// PredatorField via a per-tier table, so MulticellStage doesn't
// pass a count.
// On a hit: how long the cluster is invulnerable, the knockback
// impulse force, and the biomass loss as a fraction of the goal.
// Biomass loss is small per hit — it stings without erasing minutes
// of progress; the lost cell is the bigger sting.
const HIT_IFRAME_SEC = 0.85;
const HIT_KNOCKBACK = 380;

// ATP costs for cluster abilities. Mirrors Phase 1's costs so muscle
// memory carries over: dash 30, brake 15. Without ATP the input is a
// no-op — the cluster keeps coasting on its existing velocity.
const DASH_ATP_COST = 30;
const BRAKE_ATP_COST = 15;

// Cluster grows by one cell at each threshold. With 4 spawn cells +
// 22 growth steps the cluster tops out at 26 cells, all reachable
// before the 3M stage goal. Deltas ramp linearly from ~10k to ~250k
// so the first few grows arrive quickly (preserving the "I'm growing!"
// loop) while the late ones bite — cell #26 lands at 2.85M, leaving
// 150k of endgame headroom for storm / mega-predator pressure.
const GROWTH_THRESHOLDS = [
  10_000,    32_000,    65_000,    110_000,   165_000,
  230_000,   310_000,   400_000,   500_000,   615_000,
  740_000,   875_000,   1_020_000, 1_180_000, 1_345_000,
  1_525_000, 1_720_000, 1_920_000, 2_135_000, 2_365_000,
  2_600_000, 2_850_000,
];

export class MulticellStage implements Stage {
  readonly id = 'multicell';

  private ctx!: StageContext;
  private layer!: Container;
  private worldContainer!: Container;
  private hudLayer!: Container;
  private worldFloor!: Graphics;
  private biofield!: BiofieldMesh;
  private cluster!: Cluster;
  private cellLayer!: Container;
  private nutrients!: NutrientField;
  private obstacles!: ObstacleField;
  private predators!: PredatorField;
  private storm!: StormField;
  private particles!: ParticleSystem;
  private spotlight: CodexSpotlight | null = null;
  private touchPad: TouchPad | null = null;
  private achievements: AchievementSystem | null = null;
  private metaUpgrades: MetaUpgradeState | null = null;
  private nextGrowthIdx = 0;
  private goalReached = false;

  private biomassText!: Text;
  private biomassBarBg!: Graphics;
  private biomassBarFill!: Graphics;
  private nextCellText!: Text;
  private stageElapsed = 0;

  // DOM HUD strip — biomass / DNA / HP / ATP / timer. Same CSS as
  // Phase 1 so the two stages feel like one game. Built on init,
  // torn down in cleanup. Bus listeners are stored in `domRemovers`.
  private domHud: HTMLElement | null = null;
  private domVersion: HTMLElement | null = null;
  private hudRefs: {
    biomass?: HTMLElement; dna?: HTMLElement;
    hp?: HTMLElement; hpBar?: HTMLElement;
    atp?: HTMLElement; atpBar?: HTMLElement;
    timer?: HTMLElement;
    codexBtn?: HTMLElement;
  } = {};
  private domRemovers: Array<() => void> = [];
  private domCodexPanel: HTMLElement | null = null;
  /** codexId → rendered icon canvas; populated once at init. Same draw
   *  paths the in-game predator uses, so the codex shows the actual
   *  silhouette. Mirrors Phase 1's `sys.codexIcons` map. */
  private codexIcons = new Map<string, HTMLCanvasElement>();
  /** Lazy canvas → dataURL cache. The conversion is cheap-ish but not
   *  free, and the icon may render in both the panel and a toast. */
  private iconUrlCache = new Map<string, string>();
  private domToastsRoot: HTMLElement | null = null;
  private domGoalBanner: HTMLElement | null = null;
  private goalBannerHide = 0;
  private domDeathBanner: HTMLElement | null = null;
  private domStageComplete: HTMLElement | null = null;
  private deathBannerHide = 0;
  private isCodexOpen = false;
  // Per-session dedupe — codex:unlocked may fire multiple times for the
  // same id (a leaked Phase 1 CodexSystem listener writes to meta first;
  // we run after). Without this we'd show duplicate toasts.
  private shownCodex = new Set<string>();
  private unseenCodex = new Set<string>();

  /**
   * Pending cell-type picks. When growth thresholds are crossed
   * during gameplay, each one queues here; the picker UI consumes
   * one queued pick at a time. Carryover catch-up at init bypasses
   * this by calling cluster.addCell('core') directly so the player
   * isn't ambushed with menus on stage entry.
   */
  private pendingPicks = 0;
  private pickerRoot: Container | null = null;

  // Camera position springs toward the cluster instead of locking
  // to it — that lag is what makes mouse-following feel like
  // "moving the cluster" rather than "dragging the world".
  /** Camera zoom — widest on touch at the start of the level; pinch
   *  changes it for the rest of the level. */
  private worldZoom = 1;
  private camX = 0;
  private camY = 0;

  private minimapRoot!: Container;
  private minimapView!: Graphics;
  private minimapDot!: Graphics;

  private mouseX = 0;
  private mouseY = 0;
  private onPointerMove?: (e: PointerEvent) => void;
  private onPointerDown?: (e: PointerEvent) => void;
  private onPointerUp?: (e: PointerEvent) => void;
  private onContextMenu?: (e: MouseEvent) => void;
  /** True while the right mouse button is held — held brakes the cluster. */
  private braking = false;
  private onKeyDown?: (e: KeyboardEvent) => void;

  /** Dash cooldown HUD (small ring near the bottom-left). */
  private dashHud!: Container;
  private dashHudRing!: Graphics;
  private dashHudLabel!: Text;

  // Intro sequence — a single Phase-1-style cell at center splits into
  // 4 baby cells that become the cluster. Plays once at stage entry,
  // then waits at a "▸ CONTINUE" prompt until the player clicks. While
  // active, the world tick is gated.
  private introActive = false;
  private introT = 0;
  // When introT >= introMaxT, introReady=true and updateIntro stops
  // advancing — the final frame holds and the Continue prompt fades in.
  // The next click/key advances past the gate into gameplay.
  private readonly introMaxT = 8.0;
  private introReady = false;
  private introReadyHoldT = 0;
  private introContainer: Container | null = null;
  private introScrim: Graphics | null = null;
  private introCell: Graphics | null = null;
  private introHalo: Graphics | null = null;
  private introBabies: Graphics[] = [];
  private introTitle: Text | null = null;
  private introSubTop: Text | null = null;
  private introSubBottom: Text | null = null;
  private introContinue: Text | null = null;
  private introCarry: CarryoverPayload | null = null;
  private onIntroSkip?: (e: Event) => void;

  init(ctx: StageContext): void {
    this.ctx = ctx;
    this.layer = new Container();
    ctx.worldLayer.addChild(this.layer);
    // Defensive: Phase 1 attaches a chromatic-aberration filter to the
    // shared worldLayer. If its cleanup didn't run (or someone adds a
    // new post-filter later) we'd render this whole stage through an
    // offscreen sampler, which smears entity edges during scrolling.
    ctx.worldLayer.filters = [];

    // Phase 2 has its own backing track — Cellular_Bloom. AudioManager
    // crossfades from whatever was playing (Phase 1's track) to this one.
    bus.emit('music:setTrack', { id: 'multicell' });

    // Two top-level layers:
    //   worldContainer — camera-translated; holds biofield (bottom),
    //                    floor, cluster, food. The biofield is a Mesh
    //                    in here so it scrolls via the container
    //                    transform — no offscreen filter target, no
    //                    sub-pixel resampling smearing entity edges.
    //   hudLayer       — screen-fixed top (label, carryover, minimap)
    this.worldContainer = new Container();
    this.hudLayer = new Container();
    this.layer.addChild(this.worldContainer);
    this.layer.addChild(this.hudLayer);

    // Heritage Tree resolver + Achievement system. Install BEFORE
    // applying carryover so startingBiomass stacks with the head-start.
    this.metaUpgrades = new MetaUpgradeState(ctx.data.metaUpgrades);
    this.achievements = new AchievementSystem(ctx.data.achievements);
    player.setMetaUpgrades(this.metaUpgrades);
    this.metaUpgrades.notifyStageStart();
    this.achievements.notifyStageStart('multicell');

    // Carryover from Phase 1 — speedrun bonuses become the player's
    // starting resources for Phase 2.
    // Resuming a saved Phase 2 run (Continue / reload) restores biomass,
    // DNA and the cluster's cells instead of re-applying the carryover.
    const run = saveManager.getRun();
    const resuming = run.stageId === 'multicell' && saveManager.hasRunProgress();
    const savedCellTypes = resuming
      ? ((run.stageData.multicell?.cellTypes as CellType[] | undefined) ?? [])
      : [];
    const carry = (saveManager.getMeta().prestige?.carryover as CarryoverPayload | undefined) ?? null;
    if (resuming) {
      player.hydrateFromSave();
    } else if (carry) {
      player.dna = (carry.totalDnaCarry ?? 0);
      player.biomass = (carry.biomassHeadStart ?? 0);
      run.biomass = player.biomass;
    }
    // Phase 2 never emits dna:changed for the carryover, so mirror it
    // into meta here or a reload would come back with 0 DNA.
    saveManager.getMeta().dna = player.dna;
    this.metaUpgrades.applyRunStartBonuses();

    // Animated bioluminescent background — a world-anchored Mesh that
    // covers the full play area. Sits at the BOTTOM of worldContainer
    // so floor specks, food, predators, and the cluster all draw on
    // top of it. The shader samples in local (= world) coordinates,
    // so as the camera scrolls the field stays pinned to its world
    // landmarks instead of drifting against entities.
    this.biofield = new BiofieldMesh(WORLD_W, WORLD_H);
    this.worldContainer.addChild(this.biofield);

    this.worldFloor = new Graphics();
    this.worldContainer.addChild(this.worldFloor);
    this.drawWorldFloor();

    // Obstacle shards — sit at the bottom of the world stack so food
    // orbits and the cluster always draw over them. Created BEFORE
    // the nutrient field because food orbits need the shard list.
    const obstacleLayer = new Container();
    this.worldContainer.addChild(obstacleLayer);
    this.obstacles = new ObstacleField(
      obstacleLayer, WORLD_W, WORLD_H, OBSTACLE_COUNT,
      WORLD_W / 2, WORLD_H / 2,
    );

    // Nutrient field — pellets orbit the shards, so the field needs
    // the obstacle list to anchor each ring.
    const nutrientLayer = new Container();
    this.worldContainer.addChild(nutrientLayer);
    this.nutrients = new NutrientField(nutrientLayer, this.obstacles.list(), NUTRIENT_COUNT);

    // Predator field — sits between food and cluster so spike bodies
    // draw under the player but over the food.
    const predatorLayer = new Container();
    this.worldContainer.addChild(predatorLayer);
    this.predators = new PredatorField(
      predatorLayer, WORLD_W, WORLD_H,
      WORLD_W / 2, WORLD_H / 2,
    );

    // Build codex icons once. Predator silhouettes come from the
    // PredatorField's own draw paths so the panel shows the same shape
    // the player encounters; the multicell concept icon is a small
    // 4-cell ring rendered inline.
    this.codexIcons = this.predators.buildIconMap(ctx.app);
    this.codexIcons.set('concept.multicell', this.renderConceptIcon(ctx.app));

    // First-encounter spotlight. Phase 2 entities live in world space;
    // the finder converts world → screen using worldContainer's
    // current offset (camera position). Phase 2 nutrients have no
    // codexId, so only predators participate.
    this.spotlight = new CodexSpotlight(
      ctx.app, ctx.fxLayer, ctx.data.codex,
      (codexId) => this.findVisibleEntityByCodexId(codexId),
      this.codexIcons,
    );

    // Storm shards — rendered above predators so an incoming front
    // visually crosses the world rather than tucking behind enemies.
    const stormLayer = new Container();
    this.worldContainer.addChild(stormLayer);
    this.storm = new StormField(stormLayer, WORLD_W, WORLD_H);

    // Cluster spawns at the world's center.
    this.cellLayer = new Container();
    this.worldContainer.addChild(this.cellLayer);
    this.cluster = new Cluster(this.cellLayer, WORLD_W / 2, WORLD_H / 2);

    // FX layer rides on top of the cluster (so absorption puffs sit
    // above the body) but inside worldContainer so it parallaxes with
    // the camera. The trail-drawing branch of ParticleSystem queries
    // the ECS world for PlayerCell entities — Phase 2 has none, so it
    // no-ops harmlessly.
    const fxLayer = new Container();
    this.worldContainer.addChild(fxLayer);
    this.particles = new ParticleSystem(fxLayer);

    // Carryover may have already pushed biomass past early growth
    // thresholds — fast-forward the cluster to match instead of
    // dripping cells in the first few seconds of play.
    while (
      this.nextGrowthIdx < GROWTH_THRESHOLDS.length &&
      player.biomass >= GROWTH_THRESHOLDS[this.nextGrowthIdx]
    ) {
      this.cluster.addCell(savedCellTypes[this.nextGrowthIdx] ?? 'core');
      this.nextGrowthIdx++;
    }

    // Camera starts centered on the cluster (no lag at spawn).
    this.worldZoom = startZoom();
    this.camX = this.cluster.x;
    this.camY = this.cluster.y;

    this.mouseX = ctx.app.screen.width / 2;
    this.mouseY = ctx.app.screen.height / 2;

    this.buildDomHud();
    this.introCarry = carry;
    this.buildBiomassHud();
    this.buildDashHud();
    this.buildMinimap();
    this.updateCamera();

    // Start the cinematic intro: single Phase-1-style cell at center
    // mitoses into the 4-cell cluster. Hide the real cluster until the
    // intro hands control over. The carryover banner waits too — we
    // want the player to see the evolution moment uncluttered.
    this.introActive = true;
    this.cluster.container.alpha = 0;
    this.minimapRoot.alpha = 0;
    this.dashHud.alpha = 0;
    this.buildIntro();
    // A resumed run already saw the cinematic — hand control straight back.
    if (resuming) this.completeIntro();

    this.onPointerMove = (e: PointerEvent) => {
      const rect = ctx.app.canvas.getBoundingClientRect();
      this.mouseX = e.clientX - rect.left;
      this.mouseY = e.clientY - rect.top;
    };
    ctx.app.canvas.addEventListener('pointermove', this.onPointerMove);

    // Dash binding: space, shift, or left mouse triggers a dash toward
    // the cursor. Right mouse acts as an emergency brake (instant
    // velocity zero). Both share the same picker/input gating.
    this.onKeyDown = (e: KeyboardEvent) => {
      // Intro consumes everything via its capture-phase listener;
      // additionally bail here so post-intro the first frame doesn't
      // dispatch a stale event into gameplay logic.
      if (this.introActive) return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      // ESC closes the codex panel before any other gameplay key handling
      // so reading the codex never accidentally fires a dash on close.
      if (e.code === 'Escape') {
        if (this.isCodexOpen) {
          e.preventDefault();
          this.toggleCodexPanel(false);
        }
        return;
      }
      if (this.pickerRoot) return;
      // Diagnostic toggle: B hides the biofield bg entirely so we can
      // visually confirm the field is the only world-anchored bg layer.
      if (e.code === 'KeyB' && !e.repeat) {
        e.preventDefault();
        this.biofield.visible = !this.biofield.visible;
        return;
      }
      if (e.code !== 'Space' && e.code !== 'ShiftLeft' && e.code !== 'ShiftRight') return;
      if (e.repeat) return;
      e.preventDefault();
      this.tryDashAtCursor();
    };
    window.addEventListener('keydown', this.onKeyDown);

    // Mouse buttons: left = dash, right = hold-brake. Right-click also
    // suppresses the browser context menu over the canvas so the
    // brake doesn't surface a system menu over gameplay.
    this.onPointerDown = (e: PointerEvent) => {
      // During the intro, the capture-phase skip listener consumes the
      // event before this handler runs. Belt-and-suspenders: also gate
      // here so a race-of-listener-order can't fire a phantom dash.
      if (this.introActive) return;
      if (this.pickerRoot) return;
      if (e.button === 0) {
        e.preventDefault();
        // On touch a tap only steers — dash and brake are on the pad.
        if (!IS_TOUCH) this.tryDashAtCursor();
      } else if (e.button === 2) {
        e.preventDefault();
        this.braking = true;
        this.brakeCluster();
      }
    };
    this.onPointerUp = (e: PointerEvent) => {
      if (e.button === 2) this.braking = false;
    };
    ctx.app.canvas.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('pointerup', this.onPointerUp);
    this.onContextMenu = (e: MouseEvent) => { e.preventDefault(); };
    ctx.app.canvas.addEventListener('contextmenu', this.onContextMenu);
    // Touch: dash + hold-to-brake as on-screen buttons.
    this.touchPad = createTouchPad([
      { id: 'brake', icon: '■', label: 'BRAKE', side: 'left',
        onPress: () => { if (!this.pickerRoot && !this.introActive) { this.braking = true; this.brakeCluster(); } },
        onRelease: () => { this.braking = false; } },
      { id: 'dash', icon: '➤', label: 'DASH', side: 'right', primary: true,
        onPress: () => { if (!this.pickerRoot && !this.introActive) this.tryDashAtCursor(); } },
    ]);

    // Debug hook (same spirit as window.__evolve in main.ts).
    (window as any).__multicell = this;
  }

  private tryDashAtCursor(): void {
    // ATP-gate the input first so a low-ATP press doesn't burn the
    // cluster's dash cooldown silently. tryDash internally returns
    // false if cooldown isn't ready, so we only spend on a real fire.
    if (player.atp < DASH_ATP_COST) return;
    const sw = this.ctx.app.screen.width;
    const sh = this.ctx.app.screen.height;
    const wmx = this.cluster.x + (this.mouseX - sw / 2) / this.worldZoom;
    const wmy = this.cluster.y + (this.mouseY - sh / 2) / this.worldZoom;
    if (this.cluster.tryDash(wmx, wmy)) {
      player.spendAtp(DASH_ATP_COST);
      bus.emit('audio:cue', { id: 'pulse', volume: 0.35 });
      bus.emit('fx:particles', {
        x: this.cluster.x, y: this.cluster.y,
        count: 14, color: 0x76e4f7, spread: 80,
      });
    }
  }

  private brakeCluster(): void {
    // Brake costs ATP per engage. If the player can't afford it, the
    // brake doesn't kick in — but we leave `braking=true` alone so
    // releasing right-mouse still lifts the flag in the pointerup
    // handler. Successful brakes zero velocity and the per-frame
    // override in update() keeps it pinned while held.
    if (!player.spendAtp(BRAKE_ATP_COST)) return;
    this.cluster.vx = 0;
    this.cluster.vy = 0;
    bus.emit('audio:cue', { id: 'damage', volume: 0.18 });
  }

  update(dt: number, _elapsed: number): void {
    const sw = this.ctx.app.screen.width;
    const sh = this.ctx.app.screen.height;

    // Intro cinematic — freezes everything else while it plays. Bg
    // shader keeps animating so the screen feels alive. Particles
    // tick along too in case the intro itself emitted any.
    if (this.introActive) {
      this.updateIntro(dt);
      this.biofield.update(dt, this.cluster.x, this.cluster.y);
      this.particles.update(dt, this.ctx.world);
      return;
    }

    // Stage timer: ticks while the picker is open too — pause-on-pick
    // would feel like stopping the run, but the picker is short.
    this.updateTimer(dt);
    this.updateGoalBanner(dt);
    this.updateDeathBanner(dt);

    // Open the picker if a growth threshold queued one and nothing
    // is showing yet. The picker freezes gameplay until a choice
    // resolves, so check this before running the world tick.
    if (this.pendingPicks > 0 && !this.pickerRoot) this.openPicker();

    // While the picker is open, freeze the world but keep the bg
    // shader animating so the screen doesn't feel dead. Particles
    // are FX-only; letting them keep playing also helps the moment
    // breathe rather than snapping to a frozen frame.
    if (this.pickerRoot) {
      this.particles.update(dt, this.ctx.world);
      // Keep the bg breathing while the picker freezes the world. The
      // mesh handles its own position via the container transform —
      // we only feed it time + the cluster's world position for the
      // pulsing halo.
      this.biofield.update(dt, this.cluster.x, this.cluster.y);
      return;
    }

    // ATP recharges continuously. Same rate as Phase 1 so the player's
    // sense of how often they can dash carries over. Using stats.atpRegen
    // lets DNA upgrades later boost regen without touching the stage.
    player.regenAtp(player.stats.atpRegen * dt);

    // Mouse → world: cursor is offset from screen center, cluster
    // is at world center of view. So world target = cluster pos +
    // cursor-screen-offset. Camera-clamp doesn't matter for control;
    // if the cluster is at a wall it just won't move further.
    // Right-click brake: while held, target the cluster's own position
    // and zero its velocity so the pull-toward-cursor in cluster.update
    // can't keep accelerating it. Drains ATP while held; on empty,
    // brake disengages and movement resumes.
    let worldMouseX: number;
    let worldMouseY: number;
    if (this.braking && player.atp > 0) {
      const drainPerSec = BRAKE_ATP_COST * 2;
      player.spendAtp(Math.min(player.atp, drainPerSec * dt));
      this.cluster.vx = 0;
      this.cluster.vy = 0;
      worldMouseX = this.cluster.x;
      worldMouseY = this.cluster.y;
    } else {
      worldMouseX = this.cluster.x + (this.mouseX - sw / 2) / this.worldZoom;
      worldMouseY = this.cluster.y + (this.mouseY - sh / 2) / this.worldZoom;
    }

    this.cluster.update(dt, worldMouseX, worldMouseY);
    // Obstacles drift first so nutrients orbiting them, and any
    // collision check below, read the fresh world position.
    this.obstacles.update(dt);
    this.nutrients.update(dt, this.cluster.x, this.cluster.y, sw, sh);
    // Late-game mega-tiers (Lamprey Wraith, Tapeworm Titan) wake up
    // here when the player crosses their biomass thresholds.
    this.predators.notifyBiomass(player.biomass, this.cluster.x, this.cluster.y, this.cluster.cells.length);
    for (const u of this.predators.drainUnlocks()) {
      bus.emit('stage:goalBanner', { title: u.title, subtitle: u.subtitle, durationSec: 5 });
      bus.emit('audio:cue', { id: 'event', volume: 0.6 });
    }
    this.predators.update(dt, this.cluster.x, this.cluster.y, this.cluster.cells.length);
    // Asteroid storm — late-game timed hazard. update() drives both
    // shard motion and the warn/active/cooldown phase machine; banner
    // text emerges via drainBanners on phase change.
    this.storm.update(dt, player.biomass);
    for (const b of this.storm.drainBanners()) {
      bus.emit('stage:goalBanner', { title: b.title, subtitle: b.subtitle, durationSec: 3 });
      bus.emit('audio:cue', { id: 'event', volume: 0.55 });
    }
    this.particles.update(dt, this.ctx.world);

    // Predator collision. Threats are gated by the post-hit
    // invulnerability window (otherwise a homing predator glued to the
    // cluster would strip a cell per frame). Prey contact always
    // resolves — eating doesn't hurt — so PredatorField handles the
    // i-frame check internally and only damage strikes are skipped.
    {
      const strike = this.predators.collideWith(
        this.cluster.x, this.cluster.y, this.cluster.absorptionRadius * 0.85,
        this.cluster.cells.length,
        this.cluster.iframeT > 0,
      );
      if (strike) {
        // First contact with a tier (eaten or hit) unlocks its codex
        // entry — a parasite isn't "discovered" until it actually
        // affected the cluster.
        bus.emit('codex:unlocked', { entryId: strike.codexId });
        if (strike.type === 'prey') this.handlePredatorEaten(strike.x, strike.y, strike.biomass, strike.name);
        else this.handlePredatorHit(strike.x, strike.y, strike.name, strike.cellsLost);
      }
    }
    if (this.cluster.iframeT <= 0) {
      const crash = this.obstacles.collideWith(
        this.cluster.x, this.cluster.y, this.cluster.absorptionRadius * 0.85,
      );
      if (crash) this.handleObstacleHit(crash.x, crash.y);
    }
    // Storm shards land in the same damage path as static asteroids —
    // big bounce, lost cell, biomass tax. Gated by i-frames so the
    // player can't be ground down by a single fast-moving shard.
    if (this.cluster.iframeT <= 0) {
      const stormHit = this.storm.collideWith(
        this.cluster.x, this.cluster.y, this.cluster.absorptionRadius * 0.85,
      );
      if (stormHit) this.handleObstacleHit(stormHit.x, stormHit.y);
    }

    // Absorption: anything inside the cluster's sweep this frame is eaten.
    const hit = this.nutrients.absorbAt(this.cluster.x, this.cluster.y, this.cluster.absorptionRadius);
    if (hit.count > 0) {
      player.addBiomass(hit.biomass);
      saveManager.getRun().biomass = player.biomass;
      bus.emit('audio:pop', { pitch: 0.5 + Math.random() * 0.4, volume: 0.22 });
      // Per-hit FX, capped so a big sweep doesn't flood the screen.
      const fxCount = Math.min(hit.hits.length, 4);
      for (let i = 0; i < fxCount; i++) {
        const h = hit.hits[i];
        bus.emit('fx:particles', { x: h.x, y: h.y, count: 6, color: 0x4FD1C5, spread: 30 });
      }
      bus.emit('fx:floatingText', {
        x: this.cluster.x,
        y: this.cluster.y - this.cluster.absorptionRadius - 8,
        text: `+${hit.biomass.toLocaleString()}`,
        color: 0xC4FAF0,
      });

      // Growth: each crossed threshold queues a cell-type choice.
      // The picker drains the queue one at a time, so a sweep that
      // crosses two thresholds in one frame still shows two pickers.
      while (
        this.nextGrowthIdx < GROWTH_THRESHOLDS.length &&
        player.biomass >= GROWTH_THRESHOLDS[this.nextGrowthIdx]
      ) {
        this.nextGrowthIdx++;
        this.pendingPicks++;
        bus.emit('audio:cue', { id: 'milestone', volume: 0.4 });
        bus.emit('fx:particles', {
          x: this.cluster.x, y: this.cluster.y,
          count: 24, color: 0x4FD1C5, spread: 120,
        });
      }

      this.updateBiomassHud();

      if (!this.goalReached && player.biomass >= STAGE_GOAL_BIOMASS) this.onGoalReached();
    }

    // Hard clamp at world boundary. Zeroing the velocity component
    // prevents the cluster from "stuck-pushing" into the wall.
    if (this.cluster.x < CLUSTER_BOUND_PAD) {
      this.cluster.x = CLUSTER_BOUND_PAD;
      if (this.cluster.vx < 0) this.cluster.vx = 0;
    } else if (this.cluster.x > WORLD_W - CLUSTER_BOUND_PAD) {
      this.cluster.x = WORLD_W - CLUSTER_BOUND_PAD;
      if (this.cluster.vx > 0) this.cluster.vx = 0;
    }
    if (this.cluster.y < CLUSTER_BOUND_PAD) {
      this.cluster.y = CLUSTER_BOUND_PAD;
      if (this.cluster.vy < 0) this.cluster.vy = 0;
    } else if (this.cluster.y > WORLD_H - CLUSTER_BOUND_PAD) {
      this.cluster.y = WORLD_H - CLUSTER_BOUND_PAD;
      if (this.cluster.vy > 0) this.cluster.vy = 0;
    }

    // Deadzone follow: camera only moves when the cluster pushes
    // against the edge of a viewport-relative box. Inside the box
    // the camera is stationary, so nutrients stay pinned in screen
    // space and only the cluster moves.
    const halfW = sw * CAM_DEADZONE_FRAC_X * 0.5;
    const halfH = sh * CAM_DEADZONE_FRAC_Y * 0.5;
    const dxc = this.cluster.x - this.camX;
    const dyc = this.cluster.y - this.camY;
    if (dxc > halfW) this.camX = this.cluster.x - halfW;
    else if (dxc < -halfW) this.camX = this.cluster.x + halfW;
    if (dyc > halfH) this.camY = this.cluster.y - halfH;
    else if (dyc < -halfH) this.camY = this.cluster.y + halfH;

    this.updateCamera();
    this.updateMinimap();
    this.updateDashHud();

    // Drive the bg shader. The mesh sits inside worldContainer, so
    // its screen position is determined entirely by the container's
    // transform — the shader only needs time (for flow animation)
    // and the cluster's world position (for the bioluminescent halo).
    this.biofield.update(dt, this.cluster.x, this.cluster.y);
  }

  // --- cell-type picker ---------------------------------------------

  private openPicker(): void {
    const sw = this.ctx.app.screen.width;
    const sh = this.ctx.app.screen.height;

    const root = new Container();
    this.pickerRoot = root;
    this.hudLayer.addChild(root);

    // Dimming scrim — lets the player ignore the world while picking.
    const scrim = new Graphics();
    scrim.rect(0, 0, sw, sh).fill({ color: 0x000000, alpha: 0.55 });
    scrim.eventMode = 'static';
    root.addChild(scrim);

    const title = new Text({
      text: 'GROW A NEW CELL',
      style: { fill: 0xb8f1e2, fontSize: 14, fontWeight: '700', letterSpacing: 4 },
    });
    title.anchor.set(0.5, 0);
    title.position.set(sw / 2, sh / 2 - 160);
    root.addChild(title);

    const sub = new Text({
      text: 'Pick the cell type to add to the colony',
      style: { fill: 0xffffff, fontSize: 12 },
    });
    sub.anchor.set(0.5, 0);
    sub.position.set(sw / 2, sh / 2 - 138);
    sub.alpha = 0.7;
    root.addChild(sub);

    // Layout: 3 cards, gap 24, centered horizontally.
    const cardW = 180;
    const cardH = 220;
    const gap = 24;
    const totalW = cardW * 3 + gap * 2;
    const left = (sw - totalW) / 2;
    const top = sh / 2 - cardH / 2 + 20;

    const choices: Array<{
      type: CellType;
      title: string;
      desc: string;
      color: number;
      icon: (g: Graphics) => void;
    }> = [
      {
        type: 'armor',
        title: 'ARMOR',
        desc: `${Math.round(8 * 100) / 100}% chance to negate hits\n(stacks, capped at 75%)`,
        color: 0x8b5cf6,
        icon: (g) => {
          g.circle(0, 0, 22).stroke({ color: 0x6b46c1, alpha: 0.85, width: 3 });
          g.circle(0, 0, 28).stroke({ color: 0xa78bfa, alpha: 0.45, width: 1.5 });
          for (let i = 0; i < 6; i++) {
            const a = (i / 6) * Math.PI * 2;
            g.moveTo(Math.cos(a) * 22, Math.sin(a) * 22)
              .lineTo(Math.cos(a) * 14, Math.sin(a) * 14)
              .stroke({ color: 0x6b46c1, alpha: 0.6, width: 1.5 });
          }
          g.circle(0, 0, 12).fill({ color: 0x553c9a, alpha: 0.9 });
        },
      },
      {
        type: 'motor',
        title: 'MOTOR',
        desc: '+6% movement speed\n(stacks linearly)',
        color: 0xfde68a,
        icon: (g) => {
          g.circle(0, 0, 14).fill({ color: 0xfaf089, alpha: 0.85 });
          // tail
          let prevX = -14, prevY = 0;
          for (let i = 1; i <= 6; i++) {
            const f = i / 6;
            const x = -14 - f * 24;
            const y = Math.sin(f * 6) * 6;
            g.moveTo(prevX, prevY).lineTo(x, y).stroke({ color: 0xfde68a, alpha: 0.85, width: 2 });
            prevX = x; prevY = y;
          }
        },
      },
      {
        type: 'sensor',
        title: 'SENSOR',
        desc: '+5% absorption sweep\n(stacks linearly)',
        color: 0x4FD1C5,
        icon: (g) => {
          g.circle(0, 0, 12).fill({ color: 0x4FD1C5, alpha: 0.95 });
          g.circle(0, 0, 18).stroke({ color: 0x76e4f7, alpha: 0.85, width: 2 });
          g.circle(0, 0, 24).stroke({ color: 0xC4FAF0, alpha: 0.45, width: 1.5 });
          g.circle(0, 0, 30).stroke({ color: 0xC4FAF0, alpha: 0.2, width: 1 });
        },
      },
    ];

    for (let i = 0; i < choices.length; i++) {
      const choice = choices[i];
      const card = this.buildPickerCard(
        choice.title, choice.desc, choice.color, cardW, cardH, choice.icon,
      );
      card.position.set(left + i * (cardW + gap), top);
      card.eventMode = 'static';
      card.cursor = 'pointer';
      card.on('pointerdown', () => this.resolvePick(choice.type));
      root.addChild(card);
    }
  }

  private buildPickerCard(
    title: string,
    desc: string,
    color: number,
    w: number,
    h: number,
    icon: (g: Graphics) => void,
  ): Container {
    const card = new Container();
    const bg = new Graphics();
    bg.rect(0, 0, w, h).fill({ color: 0x0f1226, alpha: 0.96 });
    bg.rect(0, 0, w, h).stroke({ width: 1.5, color, alpha: 0.7 });
    card.addChild(bg);

    // Hover highlight: the same border but brighter, drawn on top
    // so the active card stands out without redrawing the base.
    const hi = new Graphics();
    hi.rect(0, 0, w, h).stroke({ width: 2, color, alpha: 0.95 });
    hi.alpha = 0;
    card.addChild(hi);
    card.on('pointerover', () => { hi.alpha = 1; });
    card.on('pointerout', () => { hi.alpha = 0; });

    const titleText = new Text({
      text: title,
      style: { fill: color, fontSize: 14, fontWeight: '700', letterSpacing: 3 },
    });
    titleText.anchor.set(0.5, 0);
    titleText.position.set(w / 2, 16);
    card.addChild(titleText);

    const iconG = new Graphics();
    iconG.position.set(w / 2, 88);
    icon(iconG);
    card.addChild(iconG);

    const descText = new Text({
      text: desc,
      style: { fill: 0xffffff, fontSize: 11, align: 'center', lineHeight: 16 },
    });
    descText.anchor.set(0.5, 0);
    descText.position.set(w / 2, 148);
    descText.alpha = 0.8;
    card.addChild(descText);

    return card;
  }

  private resolvePick(type: CellType): void {
    this.cluster.addCell(type);
    this.pendingPicks = Math.max(0, this.pendingPicks - 1);
    bus.emit('fx:floatingText', {
      x: this.cluster.x,
      y: this.cluster.y - this.cluster.absorptionRadius - 24,
      text: `New ${type} cell — ${this.cluster.cells.length} total`,
      color: 0xb8f1e2,
    });
    bus.emit('audio:cue', { id: 'milestone', volume: 0.4 });
    if (this.pickerRoot) {
      this.pickerRoot.destroy({ children: true });
      this.pickerRoot = null;
    }
    // If multiple thresholds queued in one frame, the next update
    // tick re-opens the picker for the next pending pick.
  }

  // --- damage --------------------------------------------------------

  private handlePredatorHit(hitX: number, hitY: number, _name: string, cellsLost: number): void {
    this.cluster.applyKnockback(hitX, hitY, HIT_KNOCKBACK);
    this.cluster.iframeT = HIT_IFRAME_SEC;

    // Armor cells roll once per hit to fully negate. On a save the
    // cell isn't lost, biomass isn't docked, and only a softer FX
    // plays — but the predator still dies and i-frames still trigger,
    // so the moment-to-moment feel of "I survived a hit" is intact.
    if (Math.random() < this.cluster.hitNegateChance) {
      bus.emit('audio:cue', { id: 'event', volume: 0.25 });
      bus.emit('fx:particles', { x: hitX, y: hitY, count: 8, color: 0xa78bfa, spread: 60 });
      bus.emit('fx:floatingText', {
        x: this.cluster.x,
        y: this.cluster.y - this.cluster.absorptionRadius - 8,
        text: 'Armor held',
        color: 0xc9a5ff,
      });
      return;
    }

    // Mega-tier strikes chew off multiple cells at once. We strip up to
    // `cellsLost` cells (capped by what the cluster actually has so a
    // 3-cell bite on a 2-cell cluster doesn't try to remove from empty)
    // and scale the biomass tax + threshold rollback by the number we
    // actually removed.
    const want = Math.max(1, cellsLost | 0);
    const removed = Math.min(want, this.cluster.cells.length);
    for (let i = 0; i < removed; i++) this.cluster.removeCell();

    // Biomass tax = the cost to *grow* each removed cell, summed. Each
    // rollback step refunds the threshold delta the player paid to earn
    // that growth. Starting cells (the initial 4) cost nothing to grow,
    // so losing one taxes nothing — nextGrowthIdx is already at 0 there.
    let loss = 0;
    for (let i = 0; i < removed; i++) {
      if (this.nextGrowthIdx > 0) {
        const idx = this.nextGrowthIdx - 1;
        const prev = idx > 0 ? GROWTH_THRESHOLDS[idx - 1] : 0;
        loss += GROWTH_THRESHOLDS[idx] - prev;
        this.nextGrowthIdx--;
      }
    }
    loss = Math.min(player.biomass, loss);
    if (loss > 0) {
      player.addBiomass(-loss);
      saveManager.getRun().biomass = player.biomass;
    }

    bus.emit('audio:cue', { id: 'event', volume: 0.35 + Math.min(0.25, removed * 0.08) });
    bus.emit('fx:particles', {
      x: hitX, y: hitY,
      count: 14 + (removed - 1) * 8,
      color: 0xff5566,
      spread: 80 + (removed - 1) * 30,
    });
    bus.emit('fx:floatingText', {
      x: this.cluster.x,
      y: this.cluster.y - this.cluster.absorptionRadius - 8,
      text: removed > 1 ? `-${removed} cells` : '-1 cell',
      color: 0xff8888,
    });
    this.updateBiomassHud();

    if (this.cluster.cells.length === 0) this.respawnAfterDeath();
  }

  private handlePredatorEaten(hitX: number, hitY: number, biomass: number, name: string): void {
    // Cluster ate something smaller than itself. Big biomass payout,
    // no damage. Keep the growth-threshold + goal-check loop in sync
    // with the pellet-eat path so eating predators progresses cells
    // and triggers the win banner the same way.
    player.addBiomass(biomass);
    saveManager.getRun().biomass = player.biomass;
    bus.emit('audio:cue', { id: 'milestone', volume: 0.45 });
    bus.emit('fx:particles', { x: hitX, y: hitY, count: 22, color: 0x60ff90, spread: 110 });
    bus.emit('fx:floatingText', {
      x: this.cluster.x,
      y: this.cluster.y - this.cluster.absorptionRadius - 8,
      text: `${name} +${biomass.toLocaleString()}`,
      color: 0xb0ffd0,
    });

    while (
      this.nextGrowthIdx < GROWTH_THRESHOLDS.length &&
      player.biomass >= GROWTH_THRESHOLDS[this.nextGrowthIdx]
    ) {
      this.nextGrowthIdx++;
      this.pendingPicks++;
      bus.emit('audio:cue', { id: 'milestone', volume: 0.4 });
      bus.emit('fx:particles', {
        x: this.cluster.x, y: this.cluster.y,
        count: 24, color: 0x4FD1C5, spread: 120,
      });
    }

    this.updateBiomassHud();

    if (!this.goalReached && player.biomass >= STAGE_GOAL_BIOMASS) this.onGoalReached();
  }

  private handleObstacleHit(hitX: number, hitY: number): void {
    // Crystal shards don't die — they sit, immutable, in the world.
    // The bounce is reflective (flipper-style) so the cluster
    // physically rebounds off the rock rather than just getting a
    // soft outward push. Predator hits keep the softer knockback.
    this.cluster.applyBounce(hitX, hitY, 0.85, 260);
    this.cluster.iframeT = HIT_IFRAME_SEC;

    if (Math.random() < this.cluster.hitNegateChance) {
      bus.emit('audio:cue', { id: 'event', volume: 0.25 });
      bus.emit('fx:particles', { x: hitX, y: hitY, count: 8, color: 0xa78bfa, spread: 60 });
      bus.emit('fx:floatingText', {
        x: this.cluster.x,
        y: this.cluster.y - this.cluster.absorptionRadius - 8,
        text: 'Armor held',
        color: 0xc9a5ff,
      });
      return;
    }

    const hadGrowthCell = this.nextGrowthIdx > 0;
    this.cluster.removeCell();

    // Biomass tax = the cost the player paid to grow this exact cell
    // (threshold[idx] - threshold[idx-1]). Initial 4 cells cost nothing.
    let loss = 0;
    if (hadGrowthCell) {
      const idx = this.nextGrowthIdx - 1;
      const prev = idx > 0 ? GROWTH_THRESHOLDS[idx - 1] : 0;
      loss = Math.min(player.biomass, GROWTH_THRESHOLDS[idx] - prev);
      this.nextGrowthIdx--;
    }
    if (loss > 0) {
      player.addBiomass(-loss);
      saveManager.getRun().biomass = player.biomass;
    }

    bus.emit('audio:cue', { id: 'event', volume: 0.35 });
    // Violet sparks instead of red — reads as "hit a rock", not "hit
    // a predator". Same shape and count so the impact feels equal.
    bus.emit('fx:particles', { x: hitX, y: hitY, count: 14, color: 0xa78bfa, spread: 80 });
    bus.emit('fx:floatingText', {
      x: this.cluster.x,
      y: this.cluster.y - this.cluster.absorptionRadius - 8,
      text: '-1 cell',
      color: 0xff8888,
    });
    this.updateBiomassHud();

    if (this.cluster.cells.length === 0) this.respawnAfterDeath();
  }

  private respawnAfterDeath(): void {
    // Death-banner overlay matches Phase 1's "Cell Absorbed" pattern so
    // the moment reads consistently across stages. The cluster respawns
    // immediately under the banner; the banner auto-hides ~2.4s later.
    if (this.domDeathBanner) {
      this.domDeathBanner.classList.add('visible');
      this.deathBannerHide = 2.4;
    }
    bus.emit('audio:cue', { id: 'event', volume: 0.6 });

    this.cluster.destroy();
    this.cluster = new Cluster(this.cellLayer, WORLD_W / 2, WORLD_H / 2);
    player.biomass = 0;
    saveManager.getRun().biomass = 0;
    this.nextGrowthIdx = 0;
    this.goalReached = false;
    this.pendingPicks = 0;
    if (this.pickerRoot) {
      this.pickerRoot.destroy({ children: true });
      this.pickerRoot = null;
    }
    // Wipe predators so unlocked mega-tiers can't camp the new 4-cell
    // cluster. They'll re-unlock as biomass climbs again.
    this.predators.resetForRebirth(this.cluster.x, this.cluster.y);
    this.camX = this.cluster.x;
    this.camY = this.cluster.y;
    this.cluster.iframeT = 2.0; // grace window so respawn isn't instant-eaten
    this.updateBiomassHud();
  }

  /**
   * Find a live predator matching `codexId` whose world position falls
   * inside the current viewport, returning its *screen-space* position
   * (world coords + worldContainer offset). Used by CodexSpotlight to
   * decide whether the first-encounter popup should fire.
   */
  private findVisibleEntityByCodexId(codexId: string): SpotlightTarget | null {
    const sw = this.ctx.app.screen.width;
    const sh = this.ctx.app.screen.height;
    const offX = this.worldContainer.position.x;
    const offY = this.worldContainer.position.y;
    const margin = 20;
    // Hard gate during the cell→cluster intro animation: no spotlights
    // (food, predators, or the concept itself) fire while the cinematic
    // is playing.
    if (this.introActive) return null;
    // After the intro, the multicell concept popup must come first.
    // Suppress every other codex spotlight until 'concept.multicell'
    // has been tutorialized, so the player gets the "you are this
    // thing now" moment before any food/predator first-encounters.
    if (codexId !== 'concept.multicell') {
      const seen = new Set(saveManager.getMeta().codexTutorialized ?? []);
      if (!seen.has('concept.multicell')) return null;
    }
    // The cluster itself is the subject for the multicell concept.
    if (codexId === 'concept.multicell') {
      if (!this.cluster) return null;
      const z = this.worldZoom;
      const sx = this.cluster.x * z + offX;
      const sy = this.cluster.y * z + offY;
      if (sx < margin || sx > sw - margin || sy < margin || sy > sh - margin) return null;
      // Approximate the cluster's outer radius from its cells so the
      // spotlight ring encloses the whole organism, not just one cell.
      let r = 30;
      for (const c of this.cluster.cells) {
        const d = Math.hypot(c.ox, c.oy) + c.radius;
        if (d > r) r = d;
      }
      return { x: sx, y: sy, radius: r * z };
    }
    if (!this.predators) return null;
    // The finder works in screen space, so hand it a camera offset that
    // already accounts for zoom (world → screen is p * z + off).
    const z = this.worldZoom;
    const hit = this.predators.findVisibleByCodexId(codexId, offX / z, offY / z, sw / z, sh / z);
    if (!hit) return null;
    return { x: hit.x * z + offX, y: hit.y * z + offY, radius: hit.radius * z };
  }

  cleanup(): void {
    if (this.onPointerMove) {
      this.ctx.app.canvas.removeEventListener('pointermove', this.onPointerMove);
    }
    if (this.onPointerDown) {
      this.ctx.app.canvas.removeEventListener('pointerdown', this.onPointerDown);
    }
    if (this.onPointerUp) {
      window.removeEventListener('pointerup', this.onPointerUp);
    }
    if (this.onContextMenu) {
      this.ctx.app.canvas.removeEventListener('contextmenu', this.onContextMenu);
    }
    if (this.onKeyDown) {
      window.removeEventListener('keydown', this.onKeyDown);
    }
    if (this.onIntroSkip) {
      window.removeEventListener('keydown', this.onIntroSkip, { capture: true } as EventListenerOptions);
      this.ctx.app.canvas.removeEventListener('pointerdown', this.onIntroSkip, { capture: true } as EventListenerOptions);
      this.onIntroSkip = undefined;
    }
    this.introActive = false;
    this.spotlight?.destroy();
    this.spotlight = null;
    this.touchPad?.destroy();
    this.touchPad = null;
    this.cluster?.destroy();
    this.nutrients?.destroy();
    this.obstacles?.destroy();
    this.predators?.destroy();
    this.storm?.destroy();
    this.particles?.destroy();
    this.achievements?.destroy(); this.achievements = null;
    this.metaUpgrades?.destroy(); this.metaUpgrades = null;
    player.setMetaUpgrades(null);
    this.destroyDomHud();
    if (this.domStageComplete) { this.domStageComplete.remove(); this.domStageComplete = null; }
    this.layer.destroy({ children: true });
  }

  onResize(w: number, h: number): void {
    this.repositionHudInfo();
    this.repositionBiomassHud();
    this.repositionDashHud();
    this.repositionMinimap();
    this.updateCamera();
    // Intro visuals were positioned absolutely from sw/sh — rebuild
    // their static positions and let updateIntro repaint dynamic state.
    if (this.introActive) this.repositionIntro();
    // Rebuild the picker on resize so the cards stay centered. The
    // picker is small enough that destroy + open is cheaper than
    // tracking a dozen sub-positions.
    if (this.pickerRoot) {
      this.pickerRoot.destroy({ children: true });
      this.pickerRoot = null;
      if (this.pendingPicks > 0) this.openPicker();
    }
  }

  // --- camera --------------------------------------------------------

  private updateCamera(): void {
    const sw = this.ctx.app.screen.width;
    const sh = this.ctx.app.screen.height;
    const z = this.worldZoom;
    // Center on the lagged camera position, not the cluster — that
    // lag is what gives the cluster room to move within the view.
    let cx = sw / 2 - this.camX * z;
    let cy = sh / 2 - this.camY * z;
    if (sw < WORLD_W * z) {
      cx = Math.max(sw - WORLD_W * z, Math.min(0, cx));
    }
    if (sh < WORLD_H * z) {
      cy = Math.max(sh - WORLD_H * z, Math.min(0, cy));
    }
    this.worldContainer.scale.set(z);
    this.worldContainer.position.set(cx, cy);
  }

  /** Pinch-zoom (mobile): the player overrides the fitted zoom. */
  zoomBy(factor: number): void {
    this.worldZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, this.worldZoom * factor));
    this.updateCamera();
  }

  // --- world floor ---------------------------------------------------

  private drawWorldFloor(): void {
    this.worldFloor.clear();
    // No floor fill — the biofield shader is the visual bed. The
    // floor layer is for world-anchored decals (specks, boundary)
    // that should parallax with the camera against the shader bg.
    const SPECK_COUNT = 220;
    let seed = 0x9E3779B1;
    const rand = () => {
      seed = (seed * 1664525 + 1013904223) | 0;
      return ((seed >>> 0) % 10_000) / 10_000;
    };
    for (let i = 0; i < SPECK_COUNT; i++) {
      const x = rand() * WORLD_W;
      const y = rand() * WORLD_H;
      const r = 1 + rand() * 1.4;
      const a = 0.05 + rand() * 0.08;
      this.worldFloor.circle(x, y, r).fill({ color: 0xaaf0e0, alpha: a });
    }

    // Inner border — only visible when the player bumps the world
    // edge. Tells them "wall, not bug".
    this.worldFloor.rect(0, 0, WORLD_W, WORLD_H).stroke({
      width: 4,
      color: 0x2a4a40,
      alpha: 0.8,
    });
  }

  // --- HUD info ------------------------------------------------------

  private renderHudInfo(carry: CarryoverPayload | null): void {
    // Carry-over caption is rendered as floating Pixi lines in the HUD
    // layer (DOM HUD owns the top strip). Skipped if there's no
    // carryover (a fresh Phase 2 start with no Phase 1 history).
    if (!carry) return;
    const lines = [
      `Carried from Phase 1`,
      `DNA ${carry.totalDnaCarry ?? 0}  ·  Biomass +${(carry.biomassHeadStart ?? 0).toLocaleString()}`,
      `${carry.speedTier ?? ''}  ×${(carry.speedMult ?? 1).toFixed(1)}`,
    ];
    for (let i = 0; i < lines.length; i++) {
      const t = new Text({
        text: lines[i],
        style: { fill: 0xffffff, fontSize: i === 0 ? 10 : 12, letterSpacing: i === 0 ? 2 : 0 },
      });
      // Pushed below the DOM HUD strip (which sits ~16px from top and
      // is roughly 60-70px tall) so the text doesn't sit under it.
      t.position.set(20, 90 + i * 16);
      t.alpha = i === 0 ? 0.45 : 0.75;
      this.hudLayer.addChild(t);
    }
  }

  private onGoalReached(): void {
    this.goalReached = true;
    bus.emit('audio:cue', { id: 'milestone', volume: 0.7 });
    bus.emit('fx:flash', { color: 0x4FD1C5, alpha: 0.3, duration: 0.5 });
    bus.emit('score:goalReached', { stageId: 'multicell' });

    // Carryover into Phase 3 — same shape Phase 1 hands to us: DNA scaled by
    // a speed tier, a biomass head start, plus the cluster's make-up so the
    // Cambrian body can start with matching parts.
    const elapsed = saveManager.getRun().scoreTime;
    const min = elapsed / 60;
    const tier = min < 25 ? { mult: 3.0, label: 'Rapid evolution' }
      : min < 40 ? { mult: 2.0, label: 'Swift evolution' }
      : min < 60 ? { mult: 1.0, label: 'Steady evolution' }
      : { mult: 0.5, label: 'Patient evolution' };
    const baseDna = player.dna;
    const speedBonusDna = Math.round(baseDna * (tier.mult - 1));
    const totalDnaCarry = baseDna + speedBonusDna;
    const biomassHeadStart = Math.min(180_000, Math.round(60_000 * tier.mult));
    const cells = {
      core: this.cluster.countOf('core'), armor: this.cluster.countOf('armor'),
      motor: this.cluster.countOf('motor'), sensor: this.cluster.countOf('sensor'),
    };
    saveManager.getMeta().prestige = {
      ...(saveManager.getMeta().prestige ?? {}),
      carryover: {
        fromStage: 'multicell', completedAt: new Date().toISOString(), elapsed,
        speedTier: tier.label, speedMult: tier.mult,
        baseDna, speedBonusDna, totalDnaCarry, biomassHeadStart, cells,
      },
    };
    saveManager.save();
    stageManager.triggerExtinctionEvent({ biomass: player.biomass, time: elapsed, dnaEarned: baseDna });
    this.showStageComplete({ elapsed, tier: tier.label, mult: tier.mult, baseDna, speedBonusDna, totalDnaCarry, biomassHeadStart, cells });
  }

  /** Stage-complete screen — same #stage-complete styles Phase 1 uses. */
  private showStageComplete(s: {
    elapsed: number; tier: string; mult: number; baseDna: number; speedBonusDna: number;
    totalDnaCarry: number; biomassHeadStart: number; cells: Record<string, number>;
  }): void {
    const mm = Math.floor(s.elapsed / 60);
    const ss = String(Math.floor(s.elapsed) % 60).padStart(2, '0');
    const row = (k: string, v: string): string =>
      `<div><div class="k">${k}</div><div class="v">${v}</div></div>`;
    const el = document.createElement('div');
    el.id = 'stage-complete';
    el.innerHTML = `
      <div class="sc-card">
        <div class="sc-sub">Stage 2 — Multicell</div>
        <h1>The colony becomes a body.</h1>
        <p style="opacity:0.78;margin-bottom:20px;max-width:460px;margin-left:auto;margin-right:auto;">
          Three million cells, one will. The Cambrian sea is warm, crowded, and hungry — and for the first time, so are you.
        </p>
        <div class="sc-stats">
          <div class="sc-section-title">This run</div>
          ${row('Cells', `${s.cells.core} core · ${s.cells.armor} armor · ${s.cells.motor} motor · ${s.cells.sensor} sensor`)}
          ${row('Time', `${mm}:${ss}`)}
          <div class="sc-section-title">Carries to Phase 3</div>
          ${row('Speed tier', `${s.tier} (×${s.mult.toFixed(1)})`)}
          ${row('DNA carried', `${s.baseDna} + ${s.speedBonusDna} bonus = ${s.totalDnaCarry}`)}
          ${row('Biomass head-start', s.biomassHeadStart.toLocaleString())}
          ${row('Starting body', 'Armor → spines · Motor → fins · Sensor → scout (3+ cells each)')}
        </div>
        <button>Evolve into Phase 3</button>
      </div>`;
    document.body.appendChild(el);
    document.body.classList.add('show-cursor');
    this.domStageComplete = el;
    requestAnimationFrame(() => el.classList.add('visible'));
    el.querySelector('button')!.addEventListener('click', () => {
      el.classList.remove('visible');
      document.body.classList.remove('show-cursor');
      bus.emit('stage:advance', undefined);
    });
  }

  private repositionHudInfo(): void {
    // Carry text is anchored top-left, no resize work needed.
  }

  private updateTimer(dt: number): void {
    // Persisted scoreboard clock + the cluster's picked cell types
    // (beyond the 4 spawn cells), so a reload rebuilds the same body.
    const run = saveManager.getRun();
    run.scoreTime += dt;
    this.stageElapsed = run.scoreTime;
    const sd = (run.stageData.multicell ??= {});
    sd.cellTypes = this.cluster.cells.slice(4).map((c) => c.type);
    if (!this.hudRefs.timer) return;
    const total = Math.floor(this.stageElapsed);
    const mm = Math.floor(total / 60).toString().padStart(2, '0');
    const ss = (total % 60).toString().padStart(2, '0');
    this.hudRefs.timer.textContent = `${mm}:${ss}`;
  }

  // --- DOM HUD strip --------------------------------------------------
  // Mirrors Phase 1's HUD: biomass / DNA / HP / ATP / timer. Reuses
  // ui.css classes so the two stages look identical. Bus listeners
  // wire live values; cleanup() removes the DOM nodes and listeners.

  private buildDomHud(): void {
    // Ensure ui.css is loaded — Phase 2 may be the first stage in a
    // resumed run, in which case CellStage hasn't imported it.
    void import('@/ui/ui.css');

    const root = this.makeEl('div', { id: 'ui', 'data-stage': 'multicell' });
    root.style.cssText =
      'position:fixed;inset:0;pointer-events:none;z-index:5;' +
      "font-family:'Inter',system-ui,sans-serif;color:#d8e3f2;";
    document.body.appendChild(root);
    this.domHud = root;

    const hud = this.makeEl('div', { id: 'hud' });
    root.appendChild(hud);

    const left = this.makeEl('div', { class: 'hud-left' });
    hud.appendChild(left);
    left.appendChild(this.hudStat('biomass', 'Biomass', '0', (v) => { this.hudRefs.biomass = v; }));
    left.appendChild(this.hudStat('dna', 'DNA', '0', (v) => { this.hudRefs.dna = v; }));
    left.appendChild(this.hudStatBar('hp', 'Health', '100/100',
      (v, b) => { this.hudRefs.hp = v; this.hudRefs.hpBar = b; }));
    left.appendChild(this.hudStatBar('atp', 'ATP', '100/100',
      (v, b) => { this.hudRefs.atp = v; this.hudRefs.atpBar = b; }));

    const right = this.makeEl('div', { class: 'hud-right' });
    hud.appendChild(right);
    const timer = this.makeEl('div', { id: 'hud-timer', class: 'hud-stat' });
    timer.textContent = '00:00';
    this.hudRefs.timer = timer;
    right.appendChild(timer);

    // Codex button — opens a side-panel listing every codex entry, the
    // unlocked ones rendered with flavor + science. Mirrors Phase 1's
    // 📖 button so the HUD layout stays consistent.
    const codexBtn = this.makeEl('button', { class: 'hud-button' });
    const codexText = this.makeEl('span', { class: 'hud-button-text' }, '\u{1F4D6}  Codex');
    const codexAlert = this.makeEl('span', { class: 'hud-alert' }, '!');
    codexBtn.appendChild(codexText);
    codexBtn.appendChild(codexAlert);
    codexBtn.addEventListener('click', () => this.toggleCodexPanel());
    this.hudRefs.codexBtn = codexBtn;
    right.appendChild(codexBtn);

    // Toasts — fire-and-forget unlock notifications, same look as Phase 1.
    this.domToastsRoot = this.makeEl('div', { id: 'codex-toasts' });
    root.appendChild(this.domToastsRoot);

    // Goal banner — center overlay used for stage objective + late-game
    // event callouts. Phase 1's UI owned it; Phase 2 builds its own
    // copy with the same id so ui.css styles apply unchanged.
    this.domGoalBanner = this.makeEl('div', { id: 'goal-banner' });
    this.domGoalBanner.appendChild(this.makeEl('div', { class: 'gb-sub' }, 'Objective'));
    this.domGoalBanner.appendChild(this.makeEl('div', { class: 'gb-title' }));
    this.domGoalBanner.appendChild(this.makeEl('div', { class: 'gb-desc' }));
    root.appendChild(this.domGoalBanner);

    // Death banner — same DOM shape Phase 1 uses ("Cell Absorbed" /
    // "Respawning…"), styled by ui.css. Phase 2 says "Multicell
    // Absorbed" so the line scans for "your colony just died".
    this.domDeathBanner = this.makeEl('div', { class: 'death-banner' });
    this.domDeathBanner.appendChild(this.makeEl('div', { class: 'db-title' }, 'Multicell Absorbed'));
    this.domDeathBanner.appendChild(this.makeEl('div', { class: 'db-sub' }, 'Reforming…'));
    root.appendChild(this.domDeathBanner);

    // Codex side-panel — right-anchored, uses Phase 1's classes verbatim.
    const panel = this.makeEl('div', { class: 'side-panel right', id: 'panel-codex' });
    const close = this.makeEl('button', { class: 'panel-close' }, '\u00D7');
    close.addEventListener('click', () => this.toggleCodexPanel(false));
    panel.appendChild(close);
    panel.appendChild(this.makeEl('h2', {}, 'Codex'));
    panel.appendChild(this.makeEl('p',
      { style: 'opacity:0.65;font-size:12px;margin-top:2px;' },
      'Everything you have encountered.'));
    panel.appendChild(this.makeEl('div', { class: 'panel-body' }));
    root.appendChild(panel);
    this.domCodexPanel = panel;

    // Version tag — bottom-right corner, identical to Phase 1.
    this.domVersion = this.makeEl('div', { id: 'version-tag' });
    this.domVersion.textContent = `v${VERSION}`;
    this.domVersion.style.cssText =
      'position:fixed;bottom:6px;right:8px;font:11px/1 Inter,system-ui,sans-serif;' +
      'color:rgba(180,220,230,0.55);letter-spacing:0.5px;pointer-events:none;' +
      'user-select:none;text-shadow:0 0 4px rgba(0,0,0,0.6);z-index:9999;';
    root.appendChild(this.domVersion);

    // Initial values from live player state.
    this.setBiomassDom(player.biomass);
    if (this.hudRefs.dna) this.hudRefs.dna.textContent = player.dna.toString();
    this.setHpDom(player.hp, player.stats.hpMax);
    this.setAtpDom(player.atp, player.stats.atpMax);

    this.domRemovers.push(bus.on('biomass:changed', ({ value }) => this.setBiomassDom(value)));
    this.domRemovers.push(bus.on('dna:changed', ({ value }) => {
      if (this.hudRefs.dna) this.hudRefs.dna.textContent = value.toString();
    }));
    this.domRemovers.push(bus.on('hp:changed', ({ value, max }) => this.setHpDom(value, max)));
    this.domRemovers.push(bus.on('atp:changed', ({ value, max }) => this.setAtpDom(value, max)));
    this.domRemovers.push(bus.on('codex:unlocked', ({ entryId }) => this.handleCodexUnlocked(entryId)));
    this.domRemovers.push(bus.on('stage:goalBanner', ({ title, subtitle, durationSec }) =>
      this.showGoalBanner(title, subtitle, durationSec)));
    this.domRemovers.push(bus.on('score:submitted', (p) => {
      if (p.stageId !== 'multicell') return;
      this.showGoalBanner('Scoreboard', describeScoreResult(p), 8);
    }));
  }

  private showGoalBanner(title: string, subtitle: string, durationSec: number): void {
    const el = this.domGoalBanner;
    if (!el) return;
    (el.querySelector('.gb-title') as HTMLElement).textContent = title;
    (el.querySelector('.gb-desc') as HTMLElement).textContent = subtitle;
    el.classList.add('visible');
    this.goalBannerHide = durationSec;
  }

  private updateGoalBanner(dt: number): void {
    if (this.goalBannerHide <= 0) return;
    this.goalBannerHide -= dt;
    if (this.goalBannerHide <= 0) this.domGoalBanner?.classList.remove('visible');
  }

  private updateDeathBanner(dt: number): void {
    if (this.deathBannerHide <= 0) return;
    this.deathBannerHide -= dt;
    if (this.deathBannerHide <= 0) this.domDeathBanner?.classList.remove('visible');
  }

  // --- codex (DOM panel + toasts) -----------------------------------

  private handleCodexUnlocked(entryId: string): void {
    if (this.shownCodex.has(entryId)) return;
    this.shownCodex.add(entryId);
    // Belt-and-suspenders write to meta. The leaked Phase 1 CodexSystem
    // (if any) also writes here — we dedupe on includes() so the second
    // write no-ops. If Phase 2 was launched fresh (no Phase 1 history),
    // this is the only writer.
    const meta = saveManager.getMeta();
    if (!meta.codexUnlocked.includes(entryId)) {
      meta.codexUnlocked.push(entryId);
      saveManager.getRun().codexUnlockedThisRun.push(entryId);
    }
    const def = this.ctx.data.codex[entryId];
    if (!def) return;
    // Only mark unseen if the panel isn't already showing — otherwise
    // the user is reading it now, so nothing is "unseen".
    if (!this.isCodexOpen) this.unseenCodex.add(entryId);
    this.showCodexToast(def);
    this.refreshCodexAlert();
    if (this.isCodexOpen) this.refreshCodexPanel();
  }

  private refreshCodexAlert(): void {
    const btn = this.hudRefs.codexBtn;
    if (!btn) return;
    btn.classList.toggle('alert', this.unseenCodex.size > 0 && !this.isCodexOpen);
  }

  private toggleCodexPanel(force?: boolean): void {
    const next = force ?? !this.isCodexOpen;
    if (next === this.isCodexOpen) return;
    this.isCodexOpen = next;
    if (next) {
      this.refreshCodexPanel();
      this.domCodexPanel?.classList.add('open');
      // Pause the world while reading — same gesture Phase 1 uses.
      bus.emit('game:paused', undefined);
      this.unseenCodex.clear();
    } else {
      this.domCodexPanel?.classList.remove('open');
      bus.emit('game:resumed', undefined);
    }
    this.refreshCodexAlert();
  }

  private refreshCodexPanel(): void {
    if (!this.domCodexPanel) return;
    const body = this.domCodexPanel.querySelector('.panel-body') as HTMLElement | null;
    if (!body) return;
    body.innerHTML = '';
    const defs = this.ctx.data.codex;
    const meta = saveManager.getMeta();
    const labels: Record<string, string> = {
      concept: 'Beginnings',
      food: 'Food & Nutrients',
      mutation: 'Mutations',
      predator: 'Predators',
      event: 'Environmental Events',
    };
    const groups: Record<string, CodexEntry[]> = {};
    for (const def of Object.values(defs)) {
      (groups[def.category] ??= []).push(def);
    }
    for (const k of Object.keys(groups)) groups[k].sort((a, b) => a.title.localeCompare(b.title));
    for (const cat of ['concept', 'food', 'mutation', 'predator', 'event']) {
      const list = groups[cat] ?? [];
      if (list.length === 0) continue;
      body.appendChild(this.makeEl('h3', {}, labels[cat] ?? cat));
      for (const entry of list) {
        const unlocked = meta.codexUnlocked.includes(entry.id);
        const head = this.makeEl('div', { class: 'codex-entry-head' });
        // Icon — only when unlocked, so the locked silhouette stays a
        // mystery (matches Phase 1's pattern).
        if (unlocked) {
          const icon = this.iconImg(entry.id, 48);
          if (icon) head.appendChild(icon);
        }
        const titleCol = this.makeEl('div', { class: 'codex-entry-title-col' });
        titleCol.appendChild(this.makeEl('div', { class: 'title' }, unlocked ? entry.title : '??? Unknown'));
        titleCol.appendChild(this.makeEl('div', { class: 'codex-entry-cat' }, entry.category));
        head.appendChild(titleCol);
        const block = this.makeEl('div', { class: `codex-entry ${unlocked ? '' : 'locked'}` });
        block.appendChild(head);
        if (unlocked) {
          block.appendChild(this.makeEl('div',
            { class: 'flavor', style: 'margin-top:6px;font-size:13px;' }, entry.flavor));
          block.appendChild(this.makeEl('div', { class: 'science' }, entry.science));
        } else {
          block.appendChild(this.makeEl('div', { class: 'flavor' }, 'Not yet encountered.'));
        }
        body.appendChild(block);
      }
    }
  }

  /**
   * Build an <img> for a codex entry's icon, or null if no icon exists
   * for that id. Mirrors Phase 1's UI.iconImg — same canvas → dataURL
   * conversion cached in iconUrlCache.
   */
  private iconImg(entryId: string, sizePx: number): HTMLImageElement | null {
    const canvas = this.codexIcons.get(entryId);
    if (!canvas) return null;
    let url = this.iconUrlCache.get(entryId);
    if (!url) {
      url = canvas.toDataURL('image/png');
      this.iconUrlCache.set(entryId, url);
    }
    const img = document.createElement('img');
    img.src = url;
    img.width = sizePx;
    img.height = sizePx;
    img.alt = '';
    img.className = 'codex-icon';
    return img;
  }

  /**
   * Render the 'concept.multicell' icon — a small 4-cell ring that
   * mirrors the cluster's spawn formation. Drawn inline (not via the
   * cluster's own paint) so it's a clean static silhouette.
   */
  private renderConceptIcon(app: import('pixi.js').Application): HTMLCanvasElement {
    const SIZE = 56;
    const c = new Container();
    const frame = new Graphics();
    frame.rect(-SIZE / 2, -SIZE / 2, SIZE, SIZE).fill({ color: 0, alpha: 0.001 });
    c.addChild(frame);
    const g = new Graphics();
    const ringR = 11;
    const cellR = 7;
    g.circle(0, 0, ringR + cellR + 2).fill({ color: 0x4FD1C5, alpha: 0.10 });
    for (let i = 0; i < 4; i++) {
      const ang = i * (Math.PI / 2) + Math.PI / 4;
      const cx = Math.cos(ang) * ringR;
      const cy = Math.sin(ang) * ringR;
      g.circle(cx, cy, cellR + 1).fill({ color: 0x4FD1C5, alpha: 0.28 });
      g.circle(cx, cy, cellR).fill({ color: 0x6ee0d4, alpha: 0.95 });
      g.circle(cx - 1.5, cy - 1.5, cellR * 0.42).fill({ color: 0xfdfdfd, alpha: 0.6 });
    }
    c.addChild(g);
    return app.renderer.extract.canvas(c) as unknown as HTMLCanvasElement;
  }

  private showCodexToast(def: CodexEntry): void {
    if (!this.domToastsRoot) return;
    const t = this.makeEl('div', { class: 'codex-toast' });
    t.appendChild(this.makeEl('span', { class: 'book' }, '\u{1F4D6}'));
    const text = this.makeEl('div', { class: 'codex-toast-text' });
    text.appendChild(this.makeEl('div', { class: 'codex-toast-label' }, 'New Codex'));
    text.appendChild(this.makeEl('div', { class: 'codex-toast-title' }, def.title));
    t.appendChild(text);
    this.domToastsRoot.appendChild(t);
    setTimeout(() => t.classList.add('fading'), 3400);
    setTimeout(() => t.remove(), 3900);
  }

  // --- intro cinematic ---------------------------------------------
  // Shown once at stage entry: a single Phase-1-style cell at center
  // splits into the cluster's 4 spawn cells. Skip with click/key.

  private buildIntro(): void {
    const sw = this.ctx.app.screen.width;
    const sh = this.ctx.app.screen.height;
    const cx = sw / 2;
    const cy = sh / 2;

    this.introContainer = new Container();
    this.hudLayer.addChild(this.introContainer);

    // Dimming scrim — drawn full-screen, alpha ramps in over the first
    // half-second so the world fade reads as "settling into the past"
    // rather than a hard cut.
    this.introScrim = new Graphics();
    this.introScrim.rect(0, 0, sw, sh).fill({ color: 0x010308, alpha: 1 });
    this.introScrim.alpha = 0;
    this.introContainer.addChild(this.introScrim);

    // Everything but the scrim goes in a content box that shrinks on short
    // screens — laid out for a desktop window, the title-to-prompt column
    // is taller than a landscape phone.
    const content = new Container();
    const k = messageScale(sh);
    content.pivot.set(cx, cy);
    content.position.set(cx, cy);
    content.scale.set(k);
    this.introContainer.addChild(content);

    // Halo behind the cell — five soft cyan rings, scales with the
    // pulsing cell. Drawn first so the body sits on top.
    this.introHalo = new Graphics();
    this.introHalo.position.set(cx, cy);
    this.introHalo.scale.set(0);
    content.addChild(this.introHalo);

    // The single cell — Phase 1 art (drawCellBody renders the same
    // wobbly membrane + organelles + vesicles the player saw in Stage 1).
    this.introCell = new Graphics();
    this.introCell.position.set(cx, cy);
    this.introCell.scale.set(0);
    content.addChild(this.introCell);

    // Pre-create the 4 babies; positions/alphas are driven each frame.
    // Final offsets (post-mitosis) match the cluster's ring-0 layout so
    // the visual hand-off to the real cluster is seamless.
    this.introBabies = [];
    for (let i = 0; i < 4; i++) {
      const g = new Graphics();
      g.position.set(cx, cy);
      g.scale.set(0);
      content.addChild(g);
      this.introBabies.push(g);
    }

    // Title block — top-line label, big title, descriptor under.
    this.introSubTop = new Text({
      text: 'EVOLUTION',
      style: { fill: 0x76e4f7, fontSize: 14, fontWeight: '700', letterSpacing: 8 },
    });
    this.introSubTop.anchor.set(0.5, 0);
    this.introSubTop.position.set(cx, cy - 200);
    this.introSubTop.alpha = 0;
    content.addChild(this.introSubTop);

    this.introTitle = new Text({
      text: 'BECOMING MULTICELLULAR',
      style: { fill: 0xC4FAF0, fontSize: 32, fontWeight: '800', letterSpacing: 4 },
    });
    this.introTitle.anchor.set(0.5, 0);
    this.introTitle.position.set(cx, cy - 170);
    this.introTitle.alpha = 0;
    content.addChild(this.introTitle);

    this.introSubBottom = new Text({
      text: 'From a single cell, life multiplies.',
      style: { fill: 0xb8f1e2, fontSize: 14, fontStyle: 'italic' },
    });
    this.introSubBottom.anchor.set(0.5, 0);
    this.introSubBottom.position.set(cx, cy + 160);
    this.introSubBottom.alpha = 0;
    content.addChild(this.introSubBottom);

    // Continue prompt — only visible after the animation completes.
    // Pulses gently so the player notices it's an interactive cue,
    // not just trailing decoration.
    this.introContinue = new Text({
      text: '\u25B8  CLICK TO CONTINUE  \u25C2',
      style: { fill: 0x76e4f7, fontSize: 14, fontWeight: '700', letterSpacing: 4 },
    });
    this.introContinue.anchor.set(0.5, 0);
    this.introContinue.position.set(cx, cy + 210);
    this.introContinue.alpha = 0;
    content.addChild(this.introContinue);

    // Skip / advance on click/key. Two-stage:
    //   1) Animation playing → snap to ready state (jump to final frame).
    //   2) Already in ready state → end the intro, gameplay starts.
    // This way an impatient player can fast-forward but still has to
    // press Continue to start, and a patient player just reads then
    // clicks once.
    this.onIntroSkip = () => {
      if (!this.introActive) return;
      if (!this.introReady) {
        this.introT = this.introMaxT;
      } else {
        this.completeIntro();
      }
    };
    window.addEventListener('keydown', this.onIntroSkip, { capture: true });
    this.ctx.app.canvas.addEventListener('pointerdown', this.onIntroSkip, { capture: true });

    // Render at t=0 so the first frame isn't black-with-no-cell.
    this.renderIntroFrame(0);
  }

  private updateIntro(dt: number): void {
    if (!this.introReady) this.introT += dt;
    else this.introReadyHoldT += dt;
    // Pass an unclamped time so the breathing pulse keeps animating after
    // we hit introMaxT. The smoothstep ramps inside renderIntroFrame all
    // saturate naturally, so phases that already finished stay finished.
    const t = this.introT + this.introReadyHoldT;
    this.renderIntroFrame(t);

    // Cluster stays hidden through the entire intro — the swap to the
    // real cluster happens in completeIntro on the player's click, masked
    // by a particle burst. Fading the cluster in during the timeline made
    // the babies look like they were vanishing right when they should
    // feel alive.

    // Continue prompt fades in once the animation reaches its end and
    // pulses gently to telegraph "click me". Don't auto-complete —
    // let the player read first.
    if (t >= this.introMaxT) {
      this.introReady = true;
      if (this.introContinue) {
        const fade = Math.min(1, this.introReadyHoldT / 0.6);
        const pulse = 0.7 + 0.3 * (0.5 + 0.5 * Math.sin(this.introReadyHoldT * 3.0));
        this.introContinue.alpha = fade * pulse;
      }
    }
  }

  private renderIntroFrame(t: number): void {
    const sw = this.ctx.app.screen.width;
    const sh = this.ctx.app.screen.height;
    const cx = sw / 2;
    const cy = sh / 2;

    // Phase milestones (seconds): scrim/cell appear → pulse → split →
    // expand → cluster handoff. Each animated value uses smoothstep
    // ramps so transitions don't look mechanical.
    // 0.0–1.2   scrim + cell scale-in, "EVOLUTION" top label fades in
    // 1.2–3.0   pulsing single cell, big title fades in
    // 3.0–5.0   cell deforms (mitosis squish), babies emerge from inside
    // 5.0–6.5   babies expand outward to ring positions; subtitle fades in
    // 6.0–7.2   single cell fades out; cluster fades in (handoff)
    // 7.2–8.0   final hold; Continue prompt fades in (driven by introReadyHoldT)
    const ss = (a: number, b: number, x: number) => {
      if (b <= a) return x >= b ? 1 : 0;
      const u = Math.max(0, Math.min(1, (x - a) / (b - a)));
      return u * u * (3 - 2 * u);
    };

    if (this.introScrim) {
      // Scrim stays up the entire intro — fades only when completeIntro
      // tears down the container. The dim background lets the cell
      // visuals pop without competing with the world bg.
      this.introScrim.alpha = ss(0, 1.0, t) * 0.78;
    }

    if (this.introSubTop) this.introSubTop.alpha = ss(0.4, 1.6, t);
    if (this.introTitle) this.introTitle.alpha = ss(1.2, 2.6, t);
    if (this.introSubBottom) this.introSubBottom.alpha = ss(5.2, 6.4, t);

    // Single cell — scales in, pulses, then squishes vertically before
    // disappearing. The squish telegraphs mitosis (cells visibly elongate
    // along the spindle axis before splitting in real cytokinesis).
    if (this.introCell && this.introHalo) {
      const scaleIn = ss(0, 1.0, t);
      const pulse = 1 + 0.06 * Math.sin(t * 3.0);
      const scaleOut = 1 - ss(5.0, 6.2, t);
      const baseScale = scaleIn * pulse * scaleOut;
      // Vertical squish during mitosis phase — wider than tall.
      const squishT = ss(3.0, 4.5, t);
      const sx = baseScale * (1 + squishT * 0.55);
      const sy = baseScale * (1 - squishT * 0.45);
      this.introCell.scale.set(sx, sy);
      // Halo follows the cell's overall size (uses base, not squish).
      this.introHalo.scale.set(baseScale * 1.05);
      this.introHalo.alpha = (1 - scaleOut * 0.5);
      this.drawIntroCellBody(this.introCell, t);
      this.drawIntroHalo(this.introHalo, t);
    }

    // Babies — emerge at the center as the parent cell squishes, then
    // travel outward. They stay alive after settling (no fade-out): a
    // gentle breathing pulse + per-baby phase offset makes the colony
    // feel like an organism waiting on the player, not a frozen frame.
    // Cluster handoff happens on the Continue click in completeIntro,
    // masked by a particle burst.
    if (this.introBabies.length === 4) {
      const emergeT = ss(4.0, 5.0, t);
      const expandT = ss(4.8, 6.2, t);
      // Final radius (intro-only): big enough that 4 cells of radius 30
      // sit nearly touching but distinct, ~3× the cluster's true ring-0
      // radius. Inflated for the cinematic.
      const finalR = 56;
      for (let i = 0; i < 4; i++) {
        const ang = i * (Math.PI / 2) + Math.PI / 4;
        const r = finalR * expandT;
        const baby = this.introBabies[i];
        // Breathing pulse — only kicks in once the baby has emerged so
        // it doesn't fight the scale-in. Per-baby phase offset prevents
        // synchronized "all 4 inhale at once" tells.
        const breath = 1 + 0.05 * Math.sin(t * 1.6 + i * 1.2) * emergeT;
        const s = emergeT * breath;
        // Tiny radial wobble — colony cells in the real game drift
        // slightly within the cluster, so a 1.5px sin offset hints at
        // the same liveness without breaking the ring.
        const wobble = 1.5 * Math.sin(t * 1.1 + i * 0.7);
        const wr = r + wobble * emergeT;
        baby.position.set(cx + Math.cos(ang) * wr, cy + Math.sin(ang) * wr);
        baby.scale.set(s);
        baby.alpha = emergeT;
        this.drawIntroBaby(baby, t + i * 0.31);
      }
    }
  }

  private drawIntroCellBody(g: Graphics, time: number): void {
    g.clear();
    // Single-cell radius is intentionally smaller than the post-mitosis
    // 4-cell footprint so the colony reads as "I grew" — not just
    // "I split". 4 babies of r=30 spread at finalR=56 fill a much
    // larger envelope than this single cell.
    const r = 42;
    // Soft outer halo via stacked low-alpha fills — same idea as
    // Phase 1's softHalo. Kept inline to avoid pulling bio.ts into
    // Phase 2's bundle just for one moment.
    for (let i = 0; i < 3; i++) {
      const a = 0.18 - i * 0.06;
      g.circle(0, 0, r * (1.2 + i * 0.18)).fill({ color: 0x4FD1C5, alpha: a });
    }
    // Membrane — wobbly outline, two layers (darker ring, brighter fill).
    const points = 24;
    const wobble = 0.04;
    const pts1: number[] = [];
    for (let i = 0; i < points; i++) {
      const a = (i / points) * Math.PI * 2;
      const n = Math.sin(a * 3 + time * 1.4) * 0.5 + Math.cos(a * 5 + time) * 0.5;
      const rr = r * 1.05 * (1 + n * wobble);
      pts1.push(Math.cos(a) * rr, Math.sin(a) * rr);
    }
    g.poly(pts1).fill({ color: 0x2ea89c, alpha: 0.45 });
    const pts2: number[] = [];
    for (let i = 0; i < points; i++) {
      const a = (i / points) * Math.PI * 2;
      const n = Math.sin(a * 3 + time * 1.4 + 0.5) * 0.5 + Math.cos(a * 5 + time * 0.7 + 0.5) * 0.5;
      const rr = r * 0.98 * (1 + n * 0.035);
      pts2.push(Math.cos(a) * rr, Math.sin(a) * rr);
    }
    g.poly(pts2).fill({ color: 0x4FD1C5, alpha: 0.78 });
    // Cytoplasm highlight — a bright lens off-center, suggests volume.
    g.circle(-r * 0.25, -r * 0.35, r * 0.55).fill({ color: 0xB2F5EA, alpha: 0.22 });
    // Nucleus — single dark blob in the center, with a glowing dot.
    g.circle(0, 0, r * 0.32).fill({ color: 0x0a3a3f, alpha: 0.7 });
    g.circle(0, 0, r * 0.18).fill({ color: 0x76e4f7, alpha: 0.55 });
    // Vesicles — drifting bright dots.
    for (let i = 0; i < 6; i++) {
      const a = i * 1.05 + time * 0.7;
      const rr = r * (0.5 + 0.13 * Math.sin(time + i));
      g.circle(Math.cos(a) * rr, Math.sin(a) * rr, r * 0.06)
        .fill({ color: 0xE6FFFA, alpha: 0.7 });
    }
  }

  private drawIntroHalo(g: Graphics, time: number): void {
    g.clear();
    const r = 90 * (1 + 0.04 * Math.sin(time * 1.8));
    for (let i = 0; i < 5; i++) {
      const a = 0.07 - i * 0.012;
      if (a <= 0) break;
      g.circle(0, 0, r * (1 + i * 0.1)).fill({ color: 0x4FD1C5, alpha: a });
    }
    g.circle(0, 0, r).stroke({ color: 0x76e4f7, alpha: 0.22, width: 1 });
  }

  private drawIntroBaby(g: Graphics, time: number): void {
    g.clear();
    // Inflated for the intro — the real cluster's ring-0 cells use
    // CELL_RADIUS=14, but the cinematic wants chunky, readable babies.
    const r = 30;
    // Halo behind body so the cell glows.
    for (let i = 0; i < 3; i++) {
      const a = 0.18 - i * 0.05;
      g.circle(0, 0, r * (1.3 + i * 0.2)).fill({ color: 0x4FD1C5, alpha: a });
    }
    g.circle(0, 0, r).fill({ color: 0x2ea89c, alpha: 0.55 });
    g.circle(0, 0, r * 0.92).fill({ color: 0x4FD1C5, alpha: 0.85 });
    g.circle(0, 0, r * 0.55).fill({ color: 0xB2F5EA, alpha: 0.35 });
    g.circle(-r * 0.3, -r * 0.3, r * 0.4).fill({ color: 0xE6FFFA, alpha: 0.55 });
    // Nucleus dot — same teal core as the parent so the babies read
    // as "made from the original".
    g.circle(0, 0, r * 0.22).fill({ color: 0x0a3a3f, alpha: 0.65 });
    g.circle(0, 0, r * 0.13).fill({ color: 0x76e4f7, alpha: 0.7 });
    // Drifting vesicles.
    for (let i = 0; i < 4; i++) {
      const a = i * 1.7 + time * 1.1;
      const rr = r * (0.4 + 0.1 * Math.sin(time + i));
      g.circle(Math.cos(a) * rr, Math.sin(a) * rr, r * 0.07)
        .fill({ color: 0xE6FFFA, alpha: 0.85 });
    }
  }

  private completeIntro(): void {
    if (!this.introActive) return;
    this.introActive = false;
    this.introT = 0;
    this.introReady = false;
    this.introReadyHoldT = 0;

    if (this.onIntroSkip) {
      window.removeEventListener('keydown', this.onIntroSkip, { capture: true } as EventListenerOptions);
      this.ctx.app.canvas.removeEventListener('pointerdown', this.onIntroSkip, { capture: true } as EventListenerOptions);
      this.onIntroSkip = undefined;
    }

    if (this.introContainer) {
      this.introContainer.destroy({ children: true });
      this.introContainer = null;
    }
    this.introScrim = null;
    this.introCell = null;
    this.introHalo = null;
    this.introBabies = [];
    this.introTitle = null;
    this.introSubTop = null;
    this.introSubBottom = null;
    this.introContinue = null;

    // Reveal everything the intro had hidden, then drop the carryover
    // banner so the player sees their bonuses while gameplay starts.
    if (this.cluster?.container) this.cluster.container.alpha = 1;
    if (this.minimapRoot) this.minimapRoot.alpha = 1;
    if (this.dashHud) this.dashHud.alpha = 1;
    this.renderHudInfo(this.introCarry);
    this.introCarry = null;

    // Audio cue — soft milestone bell so the shift to gameplay reads
    // as a triumph rather than a silent unmask.
    bus.emit('audio:cue', { id: 'milestone', volume: 0.4 });
    bus.emit('fx:particles', {
      x: this.cluster.x, y: this.cluster.y,
      count: 28, color: 0x4FD1C5, spread: 140,
    });

    // Stage-opener: drop the multicell concept into the codex (matches
    // Phase 1's concept.welcome unlock) and show the same goal-banner
    // pattern Phase 1 used so the player knows what they're aiming at.
    bus.emit('codex:unlocked', { entryId: 'concept.multicell' });
    bus.emit('stage:goalBanner', {
      title: 'Reach 3,000,000 biomass',
      subtitle: 'Grow the colony, devour parasites, weather the storm',
      durationSec: 7,
    });
  }

  private repositionIntro(): void {
    if (!this.introContainer) return;
    const sw = this.ctx.app.screen.width;
    const sh = this.ctx.app.screen.height;
    const cx = sw / 2;
    const cy = sh / 2;
    if (this.introScrim) {
      this.introScrim.clear();
      this.introScrim.rect(0, 0, sw, sh).fill({ color: 0x010308, alpha: 1 });
    }
    if (this.introHalo) this.introHalo.position.set(cx, cy);
    if (this.introCell) this.introCell.position.set(cx, cy);
    if (this.introSubTop) this.introSubTop.position.set(cx, cy - 220);
    if (this.introTitle) this.introTitle.position.set(cx, cy - 190);
    if (this.introSubBottom) this.introSubBottom.position.set(cx, cy + 160);
    if (this.introContinue) this.introContinue.position.set(cx, cy + 210);
    // Babies' positions are recomputed each frame in renderIntroFrame.
  }

  private destroyDomHud(): void {
    for (const off of this.domRemovers) off();
    this.domRemovers = [];
    this.hudRefs = {};
    // If the panel was open at teardown, lift the pause so the next
    // stage doesn't inherit a frozen world.
    if (this.isCodexOpen) {
      bus.emit('game:resumed', undefined);
      this.isCodexOpen = false;
    }
    if (this.domHud?.parentElement) this.domHud.parentElement.removeChild(this.domHud);
    this.domHud = null;
    this.domVersion = null;
    this.domCodexPanel = null;
    this.domToastsRoot = null;
    this.domGoalBanner = null;
    this.domDeathBanner = null;
  }

  private setBiomassDom(value: number): void {
    if (this.hudRefs.biomass) this.hudRefs.biomass.textContent = Math.floor(value).toLocaleString();
  }
  private setHpDom(value: number, max: number): void {
    if (this.hudRefs.hp) this.hudRefs.hp.textContent = `${Math.round(value)}/${Math.round(max)}`;
    if (this.hudRefs.hpBar) this.hudRefs.hpBar.style.width = `${(value / max) * 100}%`;
  }
  private setAtpDom(value: number, max: number): void {
    if (this.hudRefs.atp) this.hudRefs.atp.textContent = `${Math.round(value)}/${Math.round(max)}`;
    if (this.hudRefs.atpBar) this.hudRefs.atpBar.style.width = `${(value / max) * 100}%`;
  }

  private hudStat(cls: string, label: string, value: string, capture: (v: HTMLElement) => void): HTMLElement {
    const e = this.makeEl('div', { class: `hud-stat ${cls}` });
    e.appendChild(this.makeEl('span', { class: 'label' }, label));
    const v = this.makeEl('span', { class: 'value' }, value);
    e.appendChild(v);
    capture(v);
    return e;
  }
  private hudStatBar(cls: string, label: string, value: string,
                     capture: (v: HTMLElement, b: HTMLElement) => void): HTMLElement {
    const e = this.makeEl('div', { class: `hud-stat ${cls}` });
    e.appendChild(this.makeEl('span', { class: 'label' }, label));
    const v = this.makeEl('span', { class: 'value' }, value);
    e.appendChild(v);
    const bar = this.makeEl('div', { class: 'bar' });
    const span = this.makeEl('span');
    span.style.width = '100%';
    bar.appendChild(span);
    e.appendChild(bar);
    capture(v, span);
    return e;
  }
  private makeEl(tag: string, attrs: Record<string, string> = {}, text?: string): HTMLElement {
    const e = document.createElement(tag);
    for (const [k, val] of Object.entries(attrs)) {
      if (k === 'class') e.className = val;
      else e.setAttribute(k, val);
    }
    if (text) e.textContent = text;
    return e;
  }

  // --- biomass goal HUD ---------------------------------------------

  private buildBiomassHud(): void {
    // Bar + label sits at the top-center; same vertical band as the
    // stage label so the HUD has one quiet line of info up there.
    this.biomassBarBg = new Graphics();
    this.biomassBarFill = new Graphics();
    this.biomassText = new Text({
      text: '',
      style: { fill: 0xb8f1e2, fontSize: 12, fontWeight: '600', letterSpacing: 1 },
    });
    this.biomassText.anchor.set(0.5, 0);
    // "Next cell at: X biomass" — sits just below the goal bar so the
    // player has a near-term target separate from the 3M endgame line.
    this.nextCellText = new Text({
      text: '',
      style: { fill: 0xc4faf0, fontSize: 11, letterSpacing: 1 },
    });
    this.nextCellText.anchor.set(0.5, 0);
    this.nextCellText.alpha = 0.75;
    this.hudLayer.addChild(this.biomassBarBg);
    this.hudLayer.addChild(this.biomassBarFill);
    this.hudLayer.addChild(this.biomassText);
    this.hudLayer.addChild(this.nextCellText);
    this.repositionBiomassHud();
    this.updateBiomassHud();
  }

  private repositionBiomassHud(): void {
    if (!this.biomassBarBg) return;
    const sw = this.ctx.app.screen.width;
    const barW = Math.min(360, sw - 80);
    const barH = 6;
    const cx = sw / 2;
    const top = 22;
    this.biomassBarBg.clear();
    this.biomassBarBg
      .rect(cx - barW / 2, top + 18, barW, barH)
      .fill({ color: 0x000000, alpha: 0.45 })
      .stroke({ width: 1, color: 0x4FD1C5, alpha: 0.45 });
    this.biomassText.position.set(cx, top);
    if (this.nextCellText) this.nextCellText.position.set(cx, top + 30);
    this.updateBiomassHud();
  }

  private updateBiomassHud(): void {
    if (!this.biomassText || !this.biomassBarFill) return;
    const sw = this.ctx.app.screen.width;
    const barW = Math.min(360, sw - 80);
    const barH = 6;
    const cx = sw / 2;
    const top = 22;
    const pct = Math.min(1, player.biomass / STAGE_GOAL_BIOMASS);
    this.biomassText.text =
      `Biomass  ${Math.floor(player.biomass).toLocaleString()} / ${STAGE_GOAL_BIOMASS.toLocaleString()}`;
    this.biomassBarFill.clear();
    this.biomassBarFill
      .rect(cx - barW / 2, top + 18, barW * pct, barH)
      .fill({ color: 0x4FD1C5, alpha: 0.85 });
    // Below the bar: countdown to the next cell, or "fully evolved" once
    // every threshold has been crossed.
    if (this.nextCellText) {
      if (this.nextGrowthIdx < GROWTH_THRESHOLDS.length) {
        const need = GROWTH_THRESHOLDS[this.nextGrowthIdx] - player.biomass;
        this.nextCellText.text = need > 0
          ? `Next cell in ${Math.ceil(need).toLocaleString()} biomass`
          : 'Next cell ready — eat to grow';
      } else {
        this.nextCellText.text = 'Cluster fully evolved';
      }
    }
  }

  // --- dash cooldown HUD --------------------------------------------

  private buildDashHud(): void {
    this.dashHud = new Container();
    this.hudLayer.addChild(this.dashHud);
    this.dashHudRing = new Graphics();
    this.dashHud.addChild(this.dashHudRing);
    this.dashHudLabel = new Text({
      text: 'DASH  [SPACE]',
      style: { fill: 0xb8f1e2, fontSize: 11, fontWeight: '600', letterSpacing: 2 },
    });
    this.dashHudLabel.alpha = 0.7;
    this.dashHud.addChild(this.dashHudLabel);
    this.repositionDashHud();
    this.updateDashHud();
  }

  private repositionDashHud(): void {
    if (!this.dashHud) return;
    const sh = this.ctx.app.screen.height;
    this.dashHud.position.set(20, sh - 60);
    this.dashHudLabel.position.set(36, -6);
  }

  private updateDashHud(): void {
    if (!this.dashHudRing) return;
    const cd = this.cluster?.dashCooldownT ?? 0;
    const ready = cd <= 0;
    const frac = ready ? 1 : 1 - cd / 0.85;
    this.dashHudRing.clear();
    // Track ring (background)
    this.dashHudRing.circle(12, 0, 12).stroke({ color: 0x2c3a55, alpha: 0.7, width: 3 });
    // Cooldown sweep — full bright cyan when ready, partial arc while filling.
    const color = ready ? 0x76e4f7 : 0x4FD1C5;
    const alpha = ready ? 0.95 : 0.6;
    if (frac > 0.001) {
      // Pixi has no easy partial-arc stroke; approximate with a polyline.
      const segs = 24;
      const start = -Math.PI / 2;
      const end = start + Math.PI * 2 * frac;
      let prevX = 12 + Math.cos(start) * 12;
      let prevY = Math.sin(start) * 12;
      for (let i = 1; i <= segs; i++) {
        const u = i / segs;
        const a = start + (end - start) * u;
        const x = 12 + Math.cos(a) * 12;
        const y = Math.sin(a) * 12;
        this.dashHudRing.moveTo(prevX, prevY).lineTo(x, y).stroke({ color, alpha, width: 3 });
        prevX = x; prevY = y;
      }
    }
    // Inner pip — solid when ready, dim while charging.
    this.dashHudRing.circle(12, 0, 4).fill({ color, alpha: ready ? 1 : 0.4 });
    this.dashHudLabel.alpha = ready ? 0.85 : 0.45;
  }

  // --- minimap -------------------------------------------------------

  private buildMinimap(): void {
    this.minimapRoot = new Container();
    this.hudLayer.addChild(this.minimapRoot);

    const frame = new Graphics();
    frame.rect(0, 0, MINIMAP_SIZE, MINIMAP_SIZE).fill({ color: 0x000000, alpha: 0.55 });
    frame.rect(0, 0, MINIMAP_SIZE, MINIMAP_SIZE).stroke({
      width: 1,
      color: 0x4FD1C5,
      alpha: 0.6,
    });
    this.minimapRoot.addChild(frame);

    this.minimapView = new Graphics();
    this.minimapRoot.addChild(this.minimapView);

    this.minimapDot = new Graphics();
    this.minimapRoot.addChild(this.minimapDot);

    this.repositionMinimap();
    this.updateMinimap();
  }

  private repositionMinimap(): void {
    if (!this.minimapRoot) return;
    const sw = this.ctx.app.screen.width;
    const sh = this.ctx.app.screen.height;
    this.minimapRoot.position.set(
      sw - MINIMAP_SIZE - MINIMAP_PAD,
      sh - MINIMAP_SIZE - MINIMAP_PAD
    );
  }

  private updateMinimap(): void {
    if (!this.minimapView || !this.minimapDot) return;
    const sw = this.ctx.app.screen.width;
    const sh = this.ctx.app.screen.height;
    const sx = MINIMAP_SIZE / WORLD_W;
    const sy = MINIMAP_SIZE / WORLD_H;

    // Viewport rectangle — a transparent box showing what's currently
    // on-screen, scaled into the minimap.
    this.minimapView.clear();
    const vw = sw * sx;
    const vh = sh * sy;
    let vx = (this.cluster.x - sw / 2) * sx;
    let vy = (this.cluster.y - sh / 2) * sy;
    // Clamp to minimap bounds (mirrors the camera clamp visually).
    if (vx < 0) vx = 0;
    if (vy < 0) vy = 0;
    if (vx + vw > MINIMAP_SIZE) vx = MINIMAP_SIZE - vw;
    if (vy + vh > MINIMAP_SIZE) vy = MINIMAP_SIZE - vh;
    this.minimapView
      .rect(vx, vy, vw, vh)
      .fill({ color: 0xffffff, alpha: 0.04 })
      .stroke({ width: 1, color: 0xffffff, alpha: 0.22 });

    // Cluster dot — the player's own position.
    this.minimapDot.clear();
    this.minimapDot
      .circle(this.cluster.x * sx, this.cluster.y * sy, 3)
      .fill({ color: 0x4FD1C5, alpha: 1 });
  }
}
